import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import type { CartItem } from './CartContext';
import { persistPendingAnotherSlot } from '../utils/bookingAnotherSlot';

type AnotherSlotHandler = (item: Omit<CartItem, 'cartItemId'>) => boolean;

type BookingAnotherSlotContextValue = {
  registerHandler: (proId: number, handler: AnotherSlotHandler | null) => void;
  requestAnotherSlot: (item: Omit<CartItem, 'cartItemId'>) => void;
};

const BookingAnotherSlotContext = createContext<BookingAnotherSlotContextValue | undefined>(
  undefined
);

export function BookingAnotherSlotProvider({ children }: { children: React.ReactNode }) {
  const handlersRef = useRef(new Map<number, AnotherSlotHandler>());
  const navigate = useNavigate();

  const registerHandler = useCallback((proId: number, handler: AnotherSlotHandler | null) => {
    if (handler) {
      handlersRef.current.set(proId, handler);
    } else {
      handlersRef.current.delete(proId);
    }
  }, []);

  const requestAnotherSlot = useCallback(
    (item: Omit<CartItem, 'cartItemId'>) => {
      persistPendingAnotherSlot(item);
      const handler = handlersRef.current.get(item.proId);
      if (handler?.(item)) {
        return;
      }
      if (item.proSlug) {
        navigate(`/pro/${encodeURIComponent(item.proSlug)}?anotherSlot=1`);
      }
    },
    [navigate]
  );

  const value = useMemo(
    () => ({ registerHandler, requestAnotherSlot }),
    [registerHandler, requestAnotherSlot]
  );

  return (
    <BookingAnotherSlotContext.Provider value={value}>
      {children}
    </BookingAnotherSlotContext.Provider>
  );
}

export function useBookingAnotherSlot(): BookingAnotherSlotContextValue {
  const ctx = useContext(BookingAnotherSlotContext);
  if (!ctx) {
    throw new Error('useBookingAnotherSlot must be used within BookingAnotherSlotProvider');
  }
  return ctx;
}

export function useRegisterBookingAnotherSlot(
  proId: number | undefined,
  handler: AnotherSlotHandler
) {
  const { registerHandler } = useBookingAnotherSlot();
  useEffect(() => {
    if (!proId) return undefined;
    registerHandler(proId, handler);
    return () => registerHandler(proId, null);
  }, [proId, handler, registerHandler]);
}
